/**
 * Hero - Sport Rotator
 * Jersey videos are stacked color(top)+alpha-mask(bottom) files, composited
 * in real time onto a <canvas> per slide so the jersey renders with genuine
 * per-pixel transparency over whatever sits behind it (page background,
 * sport background image, anything) — no baked-in background color at all.
 *
 * Handles: canvas compositing loop, dot navigation, swipe gesture between
 * slides, auto-advance timer, tab-visibility pause, preload management
 * (only active + next slide load), and reduced-motion fallbacks. All videos
 * are silent — always muted.
 */
(function () {
  'use strict';

  function initHeroRotator(root) {
    var bgLayers = Array.prototype.slice.call(root.querySelectorAll('[data-hero-bg]'));
    var videoLayerEls = Array.prototype.slice.call(root.querySelectorAll('[data-hero-video-layer]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));

    var slideCount = videoLayerEls.length;
    if (slideCount === 0) return;

    var autoplayEnabled = root.getAttribute('data-autoplay') === 'true';
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 7000;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var currentIndex = 0;
    var timer = null;
    var isTabVisible = true;

    /* Build per-slide compositing state for slides that have a video (vs. a plain poster image) */
    var slides = videoLayerEls.map(function (layerEl) {
      var video = layerEl.querySelector('[data-hero-source]');
      var canvas = layerEl.querySelector('[data-hero-canvas]');
      return {
        layerEl: layerEl,
        video: video || null,
        canvas: canvas || null,
        ctx: null,
        bufferCanvas: null,
        bufferCtx: null,
        halfW: 0,
        halfH: 0,
        ready: false,
        rafId: null
      };
    });

    function setupSlide(slide) {
      if (!slide.video || !slide.canvas) return;
      var v = slide.video;
      slide.halfW = v.videoWidth;
      slide.halfH = v.videoHeight / 2;
      if (!slide.halfW || !slide.halfH) return;

      slide.bufferCanvas = document.createElement('canvas');
      slide.bufferCanvas.width = slide.halfW;
      slide.bufferCanvas.height = v.videoHeight;
      slide.bufferCtx = slide.bufferCanvas.getContext('2d', { willReadFrequently: true });

      slide.canvas.width = slide.halfW;
      slide.canvas.height = slide.halfH;
      slide.ctx = slide.canvas.getContext('2d');

      slide.ready = true;
    }

    function drawSlide(slide) {
      if (!slide.ready) {
        slide.rafId = requestAnimationFrame(function () {
          drawSlide(slide);
        });
        return;
      }

      slide.bufferCtx.drawImage(slide.video, 0, 0, slide.halfW, slide.video.videoHeight);
      var full = slide.bufferCtx.getImageData(0, 0, slide.halfW, slide.video.videoHeight).data;

      var out = slide.ctx.createImageData(slide.halfW, slide.halfH);
      var outData = out.data;
      var maskOffset = slide.halfW * slide.halfH * 4;
      var count = slide.halfW * slide.halfH;

      for (var i = 0; i < count; i++) {
        var ci = i * 4;
        var mi = maskOffset + ci;
        outData[ci] = full[ci];
        outData[ci + 1] = full[ci + 1];
        outData[ci + 2] = full[ci + 2];
        outData[ci + 3] = full[mi];
      }

      slide.ctx.putImageData(out, 0, 0);
      slide.rafId = requestAnimationFrame(function () {
        drawSlide(slide);
      });
    }

    function stopDrawing(slide) {
      if (slide.rafId) {
        cancelAnimationFrame(slide.rafId);
        slide.rafId = null;
      }
    }

    function setPreload(slide, mode) {
      if (slide.video && slide.video.preload !== mode) {
        slide.video.preload = mode;
      }
    }

    function applyPreloadStrategy(activeIndex) {
      slides.forEach(function (slide, i) {
        if (i === activeIndex) {
          setPreload(slide, 'auto');
        } else if (i === (activeIndex + 1) % slideCount) {
          setPreload(slide, 'metadata');
        } else {
          setPreload(slide, 'none');
        }
      });
    }

    function playSlide(slide) {
      if (!slide.video) return;
      slide.video.muted = true;

      if (!slide.readyListenerAdded) {
        slide.video.addEventListener('loadedmetadata', function () {
          setupSlide(slide);
        });
        slide.readyListenerAdded = true;
      }
      if (slide.video.readyState >= 1 && !slide.ready) {
        setupSlide(slide);
      }

      var playPromise = slide.video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
      stopDrawing(slide);
      slide.rafId = requestAnimationFrame(function () {
        drawSlide(slide);
      });
    }

    function pauseSlide(slide) {
      if (slide.video) slide.video.pause();
      stopDrawing(slide);
    }

    function goTo(index) {
      if (index === currentIndex && bgLayers[index] && bgLayers[index].classList.contains('is-active')) {
        return;
      }
      index = ((index % slideCount) + slideCount) % slideCount;

      bgLayers.forEach(function (layer, i) {
        layer.classList.toggle('is-active', i === index);
      });

      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.layerEl.classList.toggle('is-active', active);
        if (active) {
          playSlide(slide);
        } else {
          pauseSlide(slide);
        }
      });

      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      currentIndex = index;
      applyPreloadStrategy(currentIndex);
      restartTimer();
    }

    function next() {
      goTo(currentIndex + 1);
    }

    function restartTimer() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      if (autoplayEnabled && !reducedMotion && slideCount > 1 && isTabVisible) {
        timer = setInterval(next, interval);
      }
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.getAttribute('data-index'), 10) || 0;
        goTo(idx);
      });
    });

    /* Swipe between slides — no visible controls, just the gesture */
    var mediaFrame = root.querySelector('.hero-media-frame');
    if (mediaFrame && slideCount > 1) {
      var swipeStartX = 0;
      var swipeStartY = 0;
      var swipeTracking = false;
      var SWIPE_THRESHOLD = 40;

      mediaFrame.addEventListener('pointerdown', function (e) {
        swipeStartX = e.clientX;
        swipeStartY = e.clientY;
        swipeTracking = true;
      });

      mediaFrame.addEventListener('pointerup', function (e) {
        if (!swipeTracking) return;
        swipeTracking = false;
        var dx = e.clientX - swipeStartX;
        var dy = e.clientY - swipeStartY;
        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) {
            goTo(currentIndex + 1);
          } else {
            goTo(currentIndex - 1);
          }
        }
      });

      mediaFrame.addEventListener('pointercancel', function () {
        swipeTracking = false;
      });
    }

    document.addEventListener('visibilitychange', function () {
      isTabVisible = document.visibilityState === 'visible';
      if (isTabVisible) {
        playSlide(slides[currentIndex]);
        restartTimer();
      } else {
        slides.forEach(pauseSlide);
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }
    });

    /* Pause the whole rotator when scrolled off-screen, resume when back */
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              playSlide(slides[currentIndex]);
              restartTimer();
            } else {
              slides.forEach(pauseSlide);
              if (timer) {
                clearInterval(timer);
                timer = null;
              }
            }
          });
        },
        { threshold: 0.15 }
      );
      observer.observe(root);
    }

    /* Kick things off */
    slides.forEach(function (slide) {
      if (slide.video) {
        slide.video.muted = true;
        slide.video.loop = true;
        slide.video.setAttribute('playsinline', '');
        slide.video.playsInline = true;
      }
    });

    applyPreloadStrategy(0);
    playSlide(slides[0]);
    dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === 0);
    });
    restartTimer();

    /* Re-init on Shopify theme editor block select (nice-to-have for merchants editing) */
    if (Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', function (evt) {
        var idx = slides.findIndex(function (slide) {
          return slide.layerEl.contains(evt.target) || slide.layerEl === evt.target;
        });
        if (idx > -1) goTo(idx);
      });
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-hero-rotator]');
    roots.forEach(initHeroRotator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var root = evt.target.querySelector('[data-hero-rotator]');
    if (root) initHeroRotator(root);
  });
})();